import * as Sentry from '@sentry/react';
import { Lead } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  endBefore,
  getDocs,
  limit,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  startAfter,
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardContext } from '../contexts/DashboardContext';
import { CustomDataGrid } from './CustomDataGrid/CustomDataGrid';

const PAGE_SIZE = 25;

interface Props {
  query: Query<Lead>;
}

const fetchPaginatedDocs = async function <T>(
  fQuery: Query<T>,
  pageParam: QueryDocumentSnapshot<T> | null,
  direction: 'next' | 'previous' = 'next',
) {
  const collectionRef = fQuery;
  let q;

  const order = orderBy('createdAt', 'desc');

  if (direction === 'next' && pageParam) {
    q = query(collectionRef, order, startAfter(pageParam), limit(PAGE_SIZE));
  } else if (direction === 'previous' && pageParam) {
    q = query(collectionRef, order, endBefore(pageParam), limit(PAGE_SIZE));
  } else {
    q = query(collectionRef, order, limit(PAGE_SIZE));
  }

  const snapshot = await getDocs(q);
  const docs: QueryDocumentSnapshot<T>[] = snapshot.docs;

  return docs;
};

export const LeadsTable = ({ query }: Props) => {
  const navigate = useNavigate();
  const { dynamicPages, forms } = useDashboardContext();

  const {
    // status,
    data,
    // error,
    isFetching,
    // isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['leads', query],
    queryFn: (ctx) =>
      fetchPaginatedDocs(query, ctx.pageParam).catch((e) => {
        Sentry.captureException(e);
        throw e;
      }),
    getNextPageParam: (group) => group[group.length - 1],
    initialPageParam: null as QueryDocumentSnapshot<Lead> | null,
    refetchOnWindowFocus: false,
    meta: {
      persist: false,
    },
  });

  const columns: ColumnDef<QueryDocumentSnapshot<Lead>>[] = useMemo(() => {
    return [
      {
        id: 'createdAt',
        header: 'Timestamp',
        accessorFn: (row) => row.data().createdAt?.toDate(),
        size: 200,
        cell: (info) => info.getValue()?.toLocaleString(),
      },
      {
        id: 'id',
        header: 'Identifier',
        accessorFn: (row) => row.id,
        size: 250,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <div>
              <div className="font-medium text-gray-900">
                {
                  Object.values(leadData.form).find(
                    ({ label }) => label?.toLowerCase() === 'email',
                  )?.value
                }
              </div>
              <div className="mt-1 text-gray-500">{info.row.original.id}</div>
            </div>
          );
        },
      },
      {
        id: 'data',
        header: 'Data',
        accessorFn: (row) => row.data().form,
        size: 300,
        cell: (info) => {
          const leadData = info.row.original.data();
          const formatValue = (value: unknown): string => {
            if (value && typeof value === 'object') {
              const obj = value as Record<string, unknown>;
              if (obj.formattedAddress) return String(obj.formattedAddress);
              return JSON.stringify(value);
            }
            return `${value}`;
          };
          return (
            <Tooltip>
              <TooltipTrigger>
                {Object.entries(leadData.form)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([_, { value }]) => formatValue(value))
                  .join(', ')}
              </TooltipTrigger>
              <TooltipContent className="whitespace-pre-wrap">
                <p>
                  {Object.entries(leadData.form)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(
                      ([_, { label, value }]) =>
                        `${label}: ${formatValue(value)}`,
                    )
                    .join('\n')}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'url',
        header: 'Url',
        accessorFn: (row) => row.data().url,
        size: 200,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Tooltip>
              <TooltipTrigger className="max-w-full truncate">
                {leadData.url.trim()}
              </TooltipTrigger>
              <TooltipContent className="break-all">
                <p>{leadData.url}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'dynamicPage',
        header: 'Dynamic Page',
        accessorFn: (row) => row.data().campaignId,
        size: 180,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Button
              variant="ghost"
              onClick={() => {
                navigate(`../pages/${leadData.campaignId}/details`);
              }}
              className="font-normal text-blue-500 underline"
            >
              {dynamicPages?.find((d) => d.id === leadData.campaignId)?.data()
                .name ?? leadData.campaignId.slice(-5)}
            </Button>
          );
        },
      },
      {
        id: 'form',
        header: 'Form',
        accessorFn: (row) => row.data().formId,
        size: 150,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Button
              variant="ghost"
              onClick={() => {
                navigate('../forms');
              }}
              className="font-normal text-blue-500 underline"
            >
              {forms?.find((f) => f.id === leadData.formId)?.data().name ??
                leadData.formId.slice(-5)}
            </Button>
          );
        },
      },
      {
        id: 'variant',
        header: 'Variant',
        accessorFn: (row) => row.data().websiteId,
        size: 150,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Button
              variant="ghost"
              onClick={() => {
                navigate(
                  `../pages/${leadData.campaignId}?tab=details&variantId=${leadData.websiteId}`,
                );
              }}
              className="font-normal text-blue-500 underline"
            >
              {leadData.variantId.slice(-5)}
            </Button>
          );
        },
      },
    ];
  }, [dynamicPages, navigate, forms]);

  return (
    <CustomDataGrid
      logicalName="leads"
      columns={columns}
      data={data?.pages.flatMap((p) => p)}
      onReachedEnd={() => !isFetching && hasNextPage && fetchNextPage()}
      initialState={{
        columnVisibility: {
          id: false,
        },
      }}
    />
  );
};
